/**
 * Session helpers for Classic Site auth — reuses hoodieClient CSRF + cookie conventions.
 *
 * CSRF source: `csrf_token` cookie (Django CSRF_COOKIE_NAME).
 * Session source: Django sessionid cookie on hoodie API origin (sent via credentials:include).
 */

import { getCsrfToken } from "../../../api/hoodieClient";
import { buildDjangoAuthUrl } from "./djangoOrigin";
import {
  getCsrfCookieDiagnostic,
  isCsrfCookieReadableInJavaScript,
} from "./csrfDiagnostics";

export { getCsrfToken };

/** Relative hoodie API path for canonical session check */
export const SESSION_PROFILE_PATH = "hoodies/me/";

/** @returns {boolean} csrf_token visible in document.cookie on the React origin only. */
export function isCsrfCookiePresent() {
  return isCsrfCookieReadableInJavaScript();
}

export { getCsrfCookieDiagnostic, isCsrfCookieReadableInJavaScript };

/**
 * @param {number} status
 * @returns {'authenticated'|'unauthenticated'|'error'}
 */
export function interpretSessionHttpStatus(status) {
  if (status === 200) return "authenticated";
  if (status === 401 || status === 403) return "unauthenticated";
  return "error";
}

/**
 * @param {unknown} profile
 * @returns {{ displayName: string, email: string|null, username: string|null }}
 */
export function extractMemberIdentity(profile) {
  const user = profile?.user || {};
  const first = (user.first_name || "").trim();
  const last = (user.last_name || "").trim();
  const full = `${first} ${last}`.trim();
  return {
    displayName: full || user.username || user.email || "Member",
    email: user.email || null,
    username: user.username || null,
  };
}

/**
 * Full-page redirect to django-allauth login (Django host when cross-origin).
 * @param {string} [returnPath]
 * @returns {string}
 */
export function getLoginRedirectUrl(returnPath) {
  return buildDjangoAuthUrl("/accounts/login/", returnPath);
}

/**
 * ACCOUNT_LOGOUT_ON_GET = True in Django settings — GET /accounts/logout/ is valid.
 * @param {string} [returnPath]
 */
export function getLogoutRedirectUrl(returnPath = "/classic-site") {
  return buildDjangoAuthUrl("/accounts/logout/", returnPath);
}

/**
 * @param {string} [returnPath]
 */
export function getSignupRedirectUrl(returnPath) {
  return buildDjangoAuthUrl("/accounts/signup/", returnPath);
}
